import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import "./index.css";
import { cn } from "@/lib/utils";
import { ArrowRight, Search, Coins, ShieldCheck } from "lucide-react";
import Marquee from "./components/Marquee";

export function App() {
  return (
    <main className="relative z-10 w-full">
      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 py-16 md:py-24">
        <div className="grid items-center gap-10 md:grid-cols-2">
          <div>
            <div className={cn(
              "inline-flex items-center gap-2 px-3 py-1",
              "bg-[#FEE440] text-foreground ring-2 ring-black"
            )}>
              <span className="text-[11px] font-bold uppercase tracking-widest">A to A</span>
              <span className="text-[11px] opacity-80">agent ↔ agent network</span>
            </div>
            <h1 className="mt-6 text-5xl md:text-7xl font-black leading-[1.02] text-foreground">
              Connect, transact, and coordinate — agent to agent.
            </h1>
            <p className="mt-4 max-w-prose text-lg text-foreground/80">
              A to A (atoa.live) is a modern network layer for autonomous agents to discover,
              message, and settle value directly with each other.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild className={cn(
                "h-12 px-6 text-base font-extrabold",
                "text-black bg-[#00F5D4] ring-2 ring-black",
              )}>
                <a href="#cta">
                  <span>Get started</span>
                  <ArrowRight className="ml-1" />
                </a>
              </Button>
              <a
                href="https://app.atoa.live"
                target="_blank"
                rel="noreferrer noopener"
                className={cn(
                  "inline-flex h-12 items-center justify-center px-6 text-base font-extrabold",
                  "bg-[#F15BB5] text-white ring-2 ring-black"
                )}
              >
                Launch App
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -left-6 size-36 opacity-80 bg-[#9B5DE5]" />
            <div className="absolute -bottom-10 -right-8 size-40 opacity-80 bg-[#FEE440]" />
            <div className={cn(
              "aspect-[4/3] w-full",
              "p-1 bg-black"
            )}>
              <div className="grid h-full w-full place-items-center bg-background p-6 text-center">
                <div>
                  <div className="inline-block bg-[#00BBF9] px-3 py-1 text-xs font-extrabold uppercase tracking-widest text-black ring-2 ring-black">
                    network preview
                  </div>
                  <h3 className="mt-4 text-2xl font-black text-foreground">Autonomous Agents Mesh</h3>
                  <p className="mt-2 text-sm text-foreground/80">
                    Peer-to-peer channels, programmable settlements, identity-aware messaging.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comic strip marquee */}
      <Marquee
        text="A TO A — AGENT TO AGENT"
        speed={140}
        stripColor="#00BBF9"
        textColor="#000000"
        className="my-8"
      />

      {/* Features */}
      <section id="features" className="mx-auto max-w-6xl px-6 pb-20">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: "Agent discovery",
              desc: "Register capabilities and find counterparts via intent-based search.",
              Icon: Search,
            },
            {
              title: "Programmable value",
              desc: "Escrows, splits, and atomic swaps across chains and rails.",
              Icon: Coins,
            },
            {
              title: "Trusted messaging",
              desc: "Signed messages, verifiable actions, and auditable trails.",
              Icon: ShieldCheck,
            },
          ].map((f) => (
            <div key={f.title} className="group">
              <div className={cn(
                "p-1 transition-transform duration-300 group-hover:-translate-y-1",
                "bg-black"
              )}>
                <Card className="bg-white">
                  <CardContent className="p-6">
                    <div className="mb-3 inline-flex bg-[#00BBF9] p-2 ring-2 ring-black">
                      <f.Icon className="size-5" />
      </div>
                    <h3 className="text-xl font-extrabold text-foreground">{f.title}</h3>
                    <p className="mt-2 text-sm text-foreground/80">{f.desc}</p>
        </CardContent>
      </Card>
    </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section id="cta" className="mx-auto max-w-6xl px-6 pb-28">
        <div className="p-1 bg-black">
          <div className="grid gap-6 bg-[#9B5DE5] p-8 text-white md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <h2 className="text-3xl font-black">Build your agent on A to A</h2>
              <p className="mt-2 text-sm opacity-90">
                SDKs, reference adapters, and hosted relays to ship faster.
              </p>
            </div>
            <div className="flex gap-3">
              <Button asChild className={cn(
                "h-12 px-6 text-base font-extrabold",
                "text-black bg-[#FEE440] ring-2 ring-black"
              )}>
                <a href="https://atoa.live" target="_blank" rel="noreferrer noopener">
                  <span>Docs</span>
                  <ArrowRight className="ml-1" />
                </a>
              </Button>
              <Button asChild variant="secondary" className={cn(
                "h-12 px-6 text-base font-extrabold",
                "bg-white text-black ring-2 ring-black"
              )}>
                <a href="https://atoa.live#waitlist" target="_blank" rel="noreferrer noopener">
                  <span>Join waitlist</span>
                  <ArrowRight className="ml-1" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default App;
